import { type CalendarItem, type CalendarDateRangeFilter } from './types'

/**
 * The window with its ends the right way round.
 *
 * A person can set either end first, so either date can land on the wrong side
 * of the other. What they mean is the days between the two of them, whichever
 * order they named them in.
 */
export const orderedLiveRange = (
  range: CalendarDateRangeFilter
): CalendarDateRangeFilter => {
  const { from, to } = range

  return from !== undefined && to !== undefined && from > to
    ? { from: to, to: from }
    : range
}

/** Whether either end of the window was set. */
export const isLiveRangeSet = (range?: CalendarDateRangeFilter): boolean =>
  range !== undefined && (range.from !== undefined || range.to !== undefined)

/**
 * The days the item is live, however it holds them.
 *
 * Content is live from the day it is scheduled until it is taken down; content
 * with no takedown is live for its day alone. A moment runs from its start to
 * its end.
 */
const liveWindow = (item: CalendarItem) =>
  item.inMarket ?? { end: item.end, start: item.start }

/**
 * Whether the item is live at any point inside the range.
 *
 * Both ends are inclusive, and either may be missing, which leaves that side
 * open. A window written the wrong way round — which only a hand-written
 * address can be — is read the way round it was meant. Dates are compared as
 * text because they are ISO 8601 at day precision, where the alphabet and the
 * calendar agree.
 *
 * This asks the item alone, unlike a category of values, which asks the whole
 * family: a window belongs to whatever has it, and keeping a piece of content
 * because its moment runs on would draw a chip outside the days a person asked
 * for.
 */
export const matchesLiveRange = (
  item: CalendarItem,
  range: CalendarDateRangeFilter
): boolean => {
  const { from, to } = orderedLiveRange(range)
  const { end, start } = liveWindow(item)

  return (
    (to === undefined || start <= to) && (from === undefined || end >= from)
  )
}
